<template>
  <div class="thelook-collage-container max-w-4xl mx-auto mb-16 p-4">
    <!-- Card Container -->
    <div class="canvas-card bg-white rounded-[2.5rem] shadow-xl p-6 md:p-8 relative border border-gray-100 transition-all duration-500" :class="{'opacity-50 grayscale scale-95': wizardStore.unlockedStep < 5, 'opacity-100 translate-y-0': visible}">

        <!-- Locked Overlay -->
        <div v-if="wizardStore.unlockedStep < 5" class="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-[2.5rem]">
            <div class="text-5xl mb-4">🏆</div>
            <h3 class="text-xl font-bold text-gray-400">Step 4: Real-Time Match</h3>
            <p class="text-sm text-gray-400">Complete AI Preview to unlock</p>
        </div>

        <!-- Error Banner (replaces alert()) -->
        <div v-if="wizardStore.collageError" class="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-3">
            <span class="text-lg shrink-0">⚠️</span>
            <span class="font-lds-body text-sm font-medium flex-1">{{ wizardStore.collageError }}</span>
            <button @click="wizardStore.collageError = null" class="text-red-400 hover:text-red-600 font-bold shrink-0">✕</button>
        </div>

        <!-- GRID -->
        <div class="grid grid-cols-2 grid-rows-2 p-2 md:p-4 bg-gray-100 relative rounded-[2rem] md:rounded-[3.5rem] border-[8px] md:border-[16px] shadow-2xl overflow-hidden transition-all duration-500"
             :class="wizardStore.layout === 'Landscape' ? 'aspect-video' : 'aspect-square'"
             :style="{
                 gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                 gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                 gap: `${(wizardStore.spacing / 100) * 1.5}rem`,
                 borderColor: wizardStore.borderVisible ? wizardStore.borderColor : 'transparent',
                 backgroundColor: wizardStore.borderVisible ? wizardStore.borderColor : 'transparent'
             }"
        >

            <!-- Cells -->
            <div v-for="(img, index) in displayImages" :key="index"
                 class="collage-cell relative w-full h-full min-h-0 transition-all duration-700 ease-out rounded-2xl md:rounded-[2rem] bg-white border-4 shadow-sm group"
                 :class="[getImageClass(index), (wizardStore.labelAlignment === 'Outer' && !img.loading) ? 'overflow-visible' : 'overflow-hidden']"
                 :style="{
                     borderColor: wizardStore.borderVisible ? wizardStore.borderColor : 'transparent'
                 }"
            >
                <!-- Loading Skeleton -->
                <div v-if="img.loading" class="w-full h-full bg-gray-50 flex flex-col items-center justify-center gap-2">
                    <span class="text-4xl animate-bounce" :style="{ animationDelay: `${index * 0.1}s` }">✨</span>
                    <span class="text-[10px] font-bold uppercase tracking-widest text-gray-300">Synthesizing...</span>
                </div>

                <!-- Loaded Image -->
                <img v-else :src="img.src" class="w-full h-full object-cover rounded-[inherit] transform group-hover:scale-110 transition-transform duration-1000" />

                <!-- Pill Label -->
                <div v-if="!img.loading && wizardStore.labelsVisible" 
                     class="absolute left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-md rounded-full shadow-lg border-2 border-gray-900 flex items-center gap-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 z-[100]"
                     :class="[
                        wizardStore.labelAlignment === 'Inner' ? 'bottom-4' : 
                        wizardStore.labelAlignment === 'Outer' ? '-bottom-6 opacity-100 group-hover:-translate-y-2' :
                        'top-1/2 -translate-y-1/2',
                        wizardStore.labelSize === 'S' ? 'px-3 py-1 scale-75' :
                        wizardStore.labelSize === 'L' ? 'px-5 py-3 scale-110' :
                        'px-4 py-2'
                     ]"
                >
                    <span class="shrink-0" :class="wizardStore.labelSize === 'S' ? 'text-base' : wizardStore.labelSize === 'L' ? 'text-xl' : 'text-lg'">{{ img.emoji || '✨' }}</span>
                    <span class="font-black tracking-widest text-gray-900 uppercase" :class="wizardStore.labelSize === 'S' ? 'text-[8px]' : wizardStore.labelSize === 'L' ? 'text-xs' : 'text-[10px]'">{{ img.label }}</span>
                </div>
            </div>

            <!-- CENTER AVATAR OVERLAY (Matrix Style) -->
            <div v-if="wizardStore.centerVisible" 
                 class="center-avatar absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 transition-all duration-500"
                 :style="{
                     width: `${10 + (wizardStore.circleRadius / 100 * 20)}%`,
                     height: `${10 + (wizardStore.circleRadius / 100 * 20)}%`
                 }"
            >
                <div class="relative w-full h-full rounded-full border-[4px] md:border-[8px] shadow-2xl overflow-hidden bg-white ring-4 md:ring-[8px] ring-gray-100/50 transition-colors duration-500"
                     :style="{
                         borderColor: wizardStore.centerBorderColor
                     }"
                >
                    <img
                        v-if="wizardStore.centerImage"
                        :src="wizardStore.centerImage"
                        class="w-full h-full object-cover grayscale brightness-110"
                        style="object-position: center 20%;"
                    />
                    <div v-else class="w-full h-full bg-gray-200 flex items-center justify-center text-4xl">
                        👤
                    </div>
                </div>
            </div>

        </div>

        <!-- Watermark -->
        <div class="mt-3 text-center">
            <span class="text-[10px] font-semibold tracking-widest uppercase text-gray-300">Powered by emoji.photo</span>
        </div>

        <!-- Actions -->
        <div class="mt-8 flex justify-center gap-4 flex-wrap">
            <button
                @click="handleDownload"
                :disabled="isDownloading || !hasImages"
                class="bg-gray-900 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:bg-black transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <span>{{ isDownloading ? 'Saving...' : 'Download' }}</span>
                <span>⬇️</span>
            </button>

            <!-- Regenerate: available while collage is loaded; backend enforces credit check -->
            <button
                v-if="hasImages"
                @click="handleRegenerate"
                class="bg-lds-primary/10 text-lds-primary border border-lds-primary/50 px-8 py-3 rounded-full font-bold shadow-sm hover:bg-lds-primary/20 transition-colors flex items-center gap-2"
                :disabled="wizardStore.isCollageGenerating"
            >
                <span v-if="wizardStore.isCollageGenerating">Generating...</span>
                <span v-else>Regenerate</span>
                <span v-if="!wizardStore.isCollageGenerating">🔄</span>
            </button>

            <button class="bg-white text-gray-900 border border-gray-200 px-8 py-3 rounded-full font-bold shadow-sm hover:bg-gray-50 transition-colors">
                Share
            </button>
        </div>

    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { useWizardStore } from '@emoji-photo/wizard-island';

export default {
  name: 'TL_CollageIsland',
  setup() {
    const wizardStore = useWizardStore();
    const visible = ref(false);
    const isDownloading = ref(false);

    const displayImages = computed(() => {
        const raw = wizardStore.images || [];
        return raw.map((item, idx) => {
            if (!item) {
                // Loading skeleton — no external URLs
                return { loading: true, emoji: '⏳', label: `SLOT ${idx + 1}` };
            }
            if (typeof item === 'object' && item.src) {
                return { loading: false, src: item.src, emoji: item.emoji || '✨', label: item.label || `EMOTION ${idx + 1}` };
            }
            if (typeof item === 'string') { // Fallback for old strings
                return { loading: false, src: item, emoji: '✨', label: `EMOTION ${idx + 1}` };
            }
            if (item && typeof item === 'object' && item.src) {
                return { loading: false, ...item };
            }
            return { loading: true, emoji: '⏳', label: `SLOT ${idx + 1}` };
        });
    });

    const hasImages = computed(() => wizardStore.images.some(img => img !== null));

    const getImageClass = (index) => `delay-[${index * 150}ms] duration-700`;

    onMounted(() => {
        setTimeout(() => visible.value = true, 500);
    });

    const handleRegenerate = () => {
        // Credit enforcement is entirely server-side — just call the action.
        wizardStore.generateCollageAssets();
    };

    /**
     * handleDownload — composite canvas download of the 2x2 grid.
     * Draws all 4 base64 images onto a canvas and exports as PNG.
     * No external dependencies required.
     */
    const handleDownload = async () => {
        isDownloading.value = true;
        try {
            await wizardStore.downloadSettings();
        } finally {
            isDownloading.value = false;
        }
    };

    return { wizardStore, visible, displayImages, hasImages, getImageClass, handleRegenerate, handleDownload, isDownloading };
  }
}
</script>
